/**
 * Frontend mirror of backend/jungzeelandia.contenttypes.permissions_config.
 *
 * Keeps the group name strings in one place and exposes small selectors
 * over the redux `permissions` slice so components don't have to pluck
 * apart the `@vist_permissions` payload themselves.
 *
 * The redux slice (see reducers/permissions/permissions.js) stores:
 *   {
 *     data: number[],        // legacy group_ids array (left for old code)
 *     groupNames: string[],  // resolved group name strings
 *     permissions: {         // pre-computed permission booleans/lists
 *       rezepte: { read, crud },
 *       produkte: { read, crud },
 *       rohstoffe: { read, crud },
 *       management: { read, crud, ui_access },
 *       benutzer: { read, crud, ui_access },
 *       can_edit_intern_extern: boolean,
 *       rezeptgruppen_read: string[],
 *       rezeptgruppen_crud: string[],
 *     }
 *   }
 */

// Group name constants — must match permissions_config.py exactly.
export const GROUPS = {
  ADMIN: 'Administratoren',
  PI: 'Produktinformation',
  MK: 'Marketing',
  FE: 'F & E',
  PM: 'Produktmanagement',
  REZEPTE: 'Rezepte',
  BILDER: 'Bilder',
  VERWALTUNG_RP: 'Verwaltung (Rezepte, Produkte)',
  VERWALTUNG_P: 'Verwaltung (Produkte)',
  VERWALTUNG_RSB: 'Verwaltung (Rezepte, Sortiment, Bilder)',
  FB: 'Fachberater',
  FV: 'Fachverkäufer',
  BAECKER: 'Bäcker',
  PRODUKTE: 'Produkte',
  ROHSTOFFE: 'Rohstoffe',
};

// -- Redux selectors --------------------------------------------------

export const selectPermissionsState = (state) => state.permissions;
export const selectPermissions = (state) => state.permissions?.permissions;
export const selectGroupNames = (state) => state.permissions?.groupNames || [];
export const selectIsAdmin = (state) =>
  selectGroupNames(state).includes(GROUPS.ADMIN);

// -- Predicates over the permissions object ---------------------------
//
// Each takes the `permissions.permissions` object (the inner one). They
// tolerate `null` / `undefined` so callers can use them directly during
// the loading window.

export const canReadRezepte = (perms) => !!perms?.rezepte?.read;
export const canReadProdukte = (perms) => !!perms?.produkte?.read;
export const canReadRohstoffe = (perms) => !!perms?.rohstoffe?.read;

export const canCrudRezepteTable = (perms) => !!perms?.rezepte?.crud;
export const canCrudProdukteTable = (perms) => !!perms?.produkte?.crud;
export const canCrudRohstoffeTable = (perms) => !!perms?.rohstoffe?.crud;

// True if the user has CRUD on at least one Rezeptgruppe — i.e. they
// can create or edit a recipe somewhere. Use this for "Neues Rezept"
// buttons, since MK/PI/FB qualify even though they lack table CRUD.
export const canCrudAnyRezept = (perms) =>
  (perms?.rezeptgruppen_crud?.length ?? 0) > 0;

export const canReadAnyRezept = (perms) =>
  (perms?.rezeptgruppen_read?.length ?? 0) > 0;

// Mirror of backend ensure_can_crud_existing_rezept: a specific recipe is
// editable iff at least one of its Rezeptgruppen is in the user's
// rezeptgruppen_crud set, with table-CRUD roles (FE/Admin) bypassing the
// per-group check (and also covering legacy recipes with no Rezeptgruppe).
export const canCrudThisRezept = (perms, rezept) => {
  if (canCrudRezepteTable(perms)) return true;
  const allowed = perms?.rezeptgruppen_crud ?? [];
  const groupNames = rezept?.gruppen_names ?? [];
  return groupNames.some((name) => allowed.includes(name));
};

export const canEditInternExtern = (perms) =>
  !!perms?.can_edit_intern_extern;

// Management table UI access — Admin and F&E only.
export const hasManagementUiAccess = (perms) =>
  !!perms?.management?.ui_access;

// Benutzer table UI access — Admin (write) and F&E (read).
export const hasBenutzerUiAccess = (perms) =>
  !!perms?.benutzer?.ui_access;

export const canCrudBenutzer = (perms) => !!perms?.benutzer?.crud;
